import React, { useState } from "react";
import { createPageUrl } from "@/utils";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ChefHat, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({
    restaurant_name: "",
    responsible_name: "",
    email: "",
    phone: "",
    city: "",
    terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.terms) { setError("Has d'acceptar els termes i condicions."); return; }
    setLoading(true);
    setError("");

    try {
      // Create venue
      const venue = await hosys.entities.Venue.create({
        name: form.restaurant_name,
        city: form.city,
        phone: form.phone,
        email: form.email,
        venue_type: "restaurant",
        active: true,
        owner_email: form.email,
      });

      // Create VenueMember for this user
      await hosys.entities.VenueMember.create({
        venue_id: venue.id,
        user_email: form.email,
        user_name: form.responsible_name,
        role: "admin",
        active: true,
      });

      // Create trial subscription (14 days)
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 14);
      await hosys.entities.Subscription.create({
        venue_id: venue.id,
        plan: "basic",
        status: "trial",
        billing_cycle: "monthly",
        start_date: new Date().toISOString().split('T')[0],
        trial_ends_at: trialEnd.toISOString().split('T')[0],
      });

      // Save venue to user preferences and redirect to plan selection
      await hosys.auth.updateMe({ active_venue_id: venue.id, full_name: form.responsible_name });

      window.location.href = createPageUrl("SelectPlan") + `?venue_id=${venue.id}&new=1`;
    } catch (err) {
      setError("Error en el registre: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7B2D3F] to-[#9B4D5F] shadow-lg mb-4">
            <ChefHat className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Registra el teu restaurant</h1>
          <p className="text-gray-500 text-sm mt-1">14 dies de prova gratuïta, sense targeta</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="restaurant_name">Nom del restaurant *</Label>
              <Input
                id="restaurant_name"
                value={form.restaurant_name}
                onChange={e => set("restaurant_name", e.target.value)}
                placeholder="Restaurant La Bona Taula"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="responsible_name">Nom del responsable *</Label>
              <Input
                id="responsible_name"
                value={form.responsible_name}
                onChange={e => set("responsible_name", e.target.value)}
                placeholder="Joan García"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={e => set("email", e.target.value)}
                placeholder="joan@restaurant.cat"
                required
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="phone">Telèfon</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={e => set("phone", e.target.value)}
                  placeholder="977 123 456"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="city">Ciutat *</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={e => set("city", e.target.value)}
                  placeholder="Reus"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={form.terms}
                onCheckedChange={v => set("terms", v)}
                className="mt-0.5"
              />
              <Label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer leading-snug">
                Accepto els{" "}
                <a href="#" className="text-[#7B2D3F] underline">termes i condicions</a>{" "}
                i la{" "}
                <a href="#" className="text-[#7B2D3F] underline">política de privacitat</a>
              </Label>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#7B2D3F] to-[#9B4D5F] hover:from-[#6B1D2F] hover:to-[#8B3D4F] text-white rounded-xl h-11 font-semibold mt-2"
            >
              {loading ? "Creant compte..." : (
                <>Crear compte i continuar <ArrowRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Ja tens compte?{" "}
            <a href={createPageUrl("Dashboard")} className="text-[#7B2D3F] font-medium underline underline-offset-2">
              Inicia sessió
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}